import React, { useContext, useEffect, useRef, useState } from "react";
import StoryReading from "./StoryReading";
import LevelsPagination from "./LevelsPagination";

import baseURL from "../../../config/config";
import { AppStateContext } from "../../../App";
import axios from "axios";
import Student_List from "./Student_List";
import TextBookContent from "./TextBookContent";
import LoadingScreen from "../../Student/LoadingScreen";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import axiosInstance from "../../../helpers/Axios";
import { Type, Mic2 } from "lucide-react";
import ScoreAnimation from "../../ScoreAnimation/ScoreAnimation";
import { logReadingEvent, flushReadingEvents } from "../../../helpers/readingEvents";

const Reading = ({
  selectedSession,
  sessionDetails,
  setApiFlag,
  apiFlag,
  studentflag,
  setStudentFlag,
  creditsRemaining = 0,
  refreshCredits,
  onLevelChange,
  showRecordingCountdown
}) => {
  if (process.env.NODE_ENV !== "production") {
    console.debug("Reading: received creditsRemaining:", creditsRemaining);
  }
  const { access_token } = useContext(AppStateContext);
  const [students, setStudents] = useState([]);
  const [textbooks, setTextbooks] = useState([]);
  const [chapters, setChapters] = useState([]); // New state for chapters
  const [selectedTextbook, setSelectedTextbook] = useState(null);
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [socketres, setSocketres] = useState(null);
  const [Loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const [apiResponseMiddle, setapiResponseMiddle] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [stories, setStories] = useState([]);
  const [storyId, setStoryId] = useState(null);
  const [speecherror, setSpeecherror] = useState(false);
  const [readingContentError, setReadingContentError] = useState(false);
  const [deactivationError, setDeactivationError] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [showScoreAnimation, setShowScoreAnimation] = useState(null);

  const reconnectTimeoutRef = useRef(null);
  const endpointIndexRef = useRef(0);
  const WS_ENDPOINTS = [
    "/ws/classroom-audio/",
    "/ws/audio/",
    "/ws/teacher-reading/",
  ];

  // 1. Session Normalization: Create a unifiedSession object
  const activeBase = selectedSession || sessionDetails;
  const initialStandard =
    selectedSession?.standard__name || activeBase?.standard;

  const [standard, setStandard] = useState(initialStandard);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const unifiedSession = {
    id:
      selectedSession?.id ||
      sessionId ||
      activeBase?.id ||
      activeBase?.session_id,
    type: (activeBase?.type || "passage").toLowerCase(),
    standard: standard || initialStandard,
    section:
      selectedSession?.section__name ||
      activeBase?.section ||
      activeBase?.section_id ||
      "--",
    textbook_id: activeBase?.textbook_id || activeBase?.textbook__id,
    textbook_chapter:
      activeBase?.textbook_chapter || activeBase?.textbook_chapter__id,
    chapter_title:
      activeBase?.textbook_chapter__title ||
      activeBase?.chapter_title ||
      "Reading Content",
  };
  console.log("showRecordingCountdown in reading : ",showRecordingCountdown)

  const handleGradeSwitch = (newGrade) => {
    if (!newGrade || String(newGrade) === String(standard)) return;
    console.log("🚀 [Reading] handleGradeSwitch triggered with:", newGrade);

    // 1. Update grade state
    logReadingEvent(unifiedSession.id, "level_changed", {
      from_standard: standard,
      to_standard: newGrade,
    });
    setStandard(newGrade);

    // 2. Reset story index for the new grade (but keep current stories visible until new ones load)
    setCurrentStoryIndex(0);

    // 3. Sync with parent
    if (typeof onLevelChange === "function") {
      onLevelChange(newGrade);
    }
  };

  // Phase 2C: classroom-reading event log (batched, best effort)
  const eventSessionRef = useRef(null);

  useEffect(() => {
    const sid = unifiedSession.id;
    if (!sid || eventSessionRef.current === sid) return;
    eventSessionRef.current = sid;
    logReadingEvent(sid, "session_opened", {
      resumed: !!selectedSession,
      session_type: unifiedSession.type,
      standard: unifiedSession.standard,
    });
  }, [unifiedSession.id]);

  useEffect(() => {
    return () => {
      if (eventSessionRef.current) {
        logReadingEvent(eventSessionRef.current, "session_closed", {});
        flushReadingEvents();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedStudent && unifiedSession.id) {
      logReadingEvent(unifiedSession.id, "student_selected", {
        student_id: selectedStudent,
      });
    }
  }, [selectedStudent]);

  useEffect(() => {
    if (storyId && unifiedSession.id) {
      logReadingEvent(unifiedSession.id, "passage_shown", {
        reading_content_id: storyId,
        student_id: selectedStudent || null,
      });
    }
  }, [storyId]);

  // Remove the WebSocket error handler for speech errors since we're handling it in ClassRecording
  useEffect(() => {
    const setupWebSocket = () => {
      if (!access_token) {
        console.log("Audio WebSocket: No access token available");
        return;
      }

      const endpoint = WS_ENDPOINTS[endpointIndexRef.current];
      // Construct the WebSocket URL specifically for this endpoint as requested
      // The user wants: wss://staging.praccel.com/ws/classroom-audio/?token=<your_jwt_token>
      const domain = baseURL?.replace(/^https?:\/\//, "").replace(/\/$/, "");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${domain}${endpoint}?token=${access_token}`;

      console.log(
        `Audio WebSocket: Attempting connection ${endpointIndexRef.current + 1}/${WS_ENDPOINTS.length} to ${wsUrl}`,
      );

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log(`Audio WebSocket: Connected successfully to ${endpoint}`);
          setWsStatus("connected");

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("🔍 [WS Debug] Message received:", data);

            if (data.type === "error") {
              console.error(
                "🔍 [WS Debug] Server returned analysis error:",
                data.message || data,
              );
            }

            if (data.detail === "Recording Submitted Successfully.") {
              console.log(
                "🔍 [WS Debug] Success condition met. Payload:",
                data.submitted_assignment || data,
              );
              setSocketres(data); // Pass full message for detail and metadata check
              setLoading(false);
              setapiResponseMiddle(data);
              setApiFlag((prev) => !prev);

              // Trigger Score Animation
              const score = data.score ?? data.submitted_assignment?.score;
              if (typeof score === "number") {
                setShowScoreAnimation(score);
              }
            } else if (data.detail === "Something Went Wrong") {
              console.error(
                "🔍 [WS Debug] Backend reported processing failure:",
                data.error,
              );
              setLoading(false);
              // We don't call handleSuccessGlobal since it failed
            }
          } catch (err) {
            console.error("Error parsing Audio WebSocket message:", err);
            setLoading(false);
          }
        };

        ws.onerror = (error) => {
          console.error(`Audio WebSocket: Error on ${endpoint}`, error);
          setWsStatus("error");
        };

        ws.onclose = (event) => {
          console.log(
            `Audio WebSocket: Closed ${endpoint} (code: ${event.code})`,
          );
          setWsStatus("disconnected");

          // Triple Fallback Logic: If closed unexpectedly, try next endpoint
          if (event.code !== 1000 && event.code !== 1001) {
            endpointIndexRef.current =
              (endpointIndexRef.current + 1) % WS_ENDPOINTS.length;
            const delay = endpointIndexRef.current === 0 ? 5000 : 1000; // Longer delay if circling back

            reconnectTimeoutRef.current = setTimeout(() => {
              console.log(`Audio WebSocket: Falling back to next endpoint...`);
              setupWebSocket();
            }, delay);
          }
        };
      } catch (error) {
        console.error("Audio WebSocket: Creation failed", error);
        setWsStatus("error");
      }
    };

    endpointIndexRef.current = 0; // Reset index on mount
    if (access_token) {
      setupWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [access_token]);

  // Reset evaluation result and error states when context (student, story, textbook, or grade) changes
  useEffect(() => {
    setSocketres(null);
    setapiResponseMiddle(null);
    setSpeecherror(false);
    setReadingContentError(false);
  }, [selectedStudent, storyId, selectedTextbook, standard]);

  // Main data fetching logic for session and textbooks
  useEffect(() => {
    const fetchData = async () => {
      try {
        let currentSessionId = selectedSession?.id || sessionId;
        let standardValue =
          selectedSession?.standard || sessionDetails?.standard || standard;
        let textbookId, textbookChapterId;

        const activeSessionLocal = selectedSession || sessionDetails;
        console.log("[Reading] fetchData start", {
          hasSelectedSession: !!selectedSession,
          hasSessionDetails: !!sessionDetails,
          activeSessionLocal,
        });

        if (!selectedSession && sessionDetails) {
          // Use section_id directly from sessionDetails (from /api/teachers/standard-sections/)
          const sectionId =
            sessionDetails.section_id || sessionDetails.section || "--";

          console.log("[Reading] Creating new session with:", {
            standard: sessionDetails.standard,
            section: sectionId,
            type: sessionDetails.type,
            textbook_chapter:
              sessionDetails.textbook_chapter ||
              (sessionDetails.textbook?.chapters?.[0]?.id ?? null),
            textbook_id: sessionDetails.textbook_id,
          });

          // Now use the section_id for the create session call
          const sessionResponse = await axiosInstance.post(
            `${baseURL}/api/classroom/create-session`,
            {
              standard: sessionDetails.standard,
              section: sectionId,
              type: sessionDetails.type,
              // Use chapter id for textbook_chapter
              textbook_chapter:
                sessionDetails.textbook_chapter ||
                (sessionDetails.textbook?.chapters?.[0]?.id ?? null),
              textbook_id: sessionDetails.textbook_id, // Pass textbook_id if available
            },
          );

          const data = sessionResponse.data.data;
          currentSessionId = data.session_id;
          standardValue = data.standard;
          textbookId = data.textbook_id;
          textbookChapterId = data.textbook_chapter;

          console.log("[Reading] New session created:", {
            sessionId: currentSessionId,
            standard: standardValue,
            textbookId,
            textbookChapterId,
          });

          // Removed: setSessionId(currentSessionId);
          // Removed: setStandard(standardValue);

          // Fetch chapters using textbook_id and textbook_chapter from response
          if (textbookId && textbookChapterId) {
            try {
              const chaptersResponse = await axiosInstance.get(
                `${baseURL}/api/classroom/get-classroom-chapters/?textbook_id=${textbookId}&chapter_id=${textbookChapterId}`,
              );
              setChapters(chaptersResponse.data.data);
              console.log(
                "[Reading] Chapters fetched after create-session",
                chaptersResponse.data.data,
              );
            } catch (chapterErr) {
              console.error("Error fetching chapters:", chapterErr);
            }
          }
        } else if (selectedSession) {
          // For resumed sessions, use the existing session ID
          console.log("[Reading] Using existing session:", {
            sessionId: selectedSession.id,
            type: selectedSession.type,
            sessionObject: selectedSession,
          });

          currentSessionId = selectedSession.id;
          standardValue = selectedSession.standard; // Extract standard for sync

          // If it's a textbook session, fetch chapters
          if (selectedSession.type === "textbook") {
            const textbookIdParam =
              selectedSession.textbook_id ||
              selectedSession.textbook__id ||
              null;
            const chapterIdParam =
              selectedSession.textbook_chapter ||
              selectedSession.textbook_chapter__id ||
              null;

            console.log("[Reading] Existing selectedSession textbook params", {
              textbookIdParam,
              chapterIdParam,
              selectedSession,
            });

            if (textbookIdParam && chapterIdParam) {
              try {
                const chaptersResponse = await axiosInstance.get(
                  `${baseURL}/api/classroom/get-classroom-chapters/?textbook_id=${textbookIdParam}&chapter_id=${chapterIdParam}`,
                );
                setChapters(chaptersResponse.data.data);
                console.log(
                  "[Reading] Chapters fetched for existing selectedSession",
                  chaptersResponse.data.data,
                );
              } catch (errChSel) {
                console.error(
                  "[Reading] Error fetching chapters for selectedSession",
                  errChSel,
                );
              }
            } else {
              console.log(
                "[Reading] selectedSession missing textbook/chapter ids",
                { selectedSession },
              );
            }
          }
        }

        // Log the final session ID being used
        console.log("[Reading] Final session ID to be used:", currentSessionId);

        // 🔥 CRITICAL: Update state for both new and resumed sessions
        if (currentSessionId) setSessionId(currentSessionId);
        if (standardValue) setStandard(standardValue);
      } catch (error) {
        console.error("Error fetching data:", error);
        console.error("Error details:", error.response?.data);

        // Check for deactivation error
        if (
          error.response?.data?.data?.error?.includes("has been deactivated")
        ) {
          setDeactivationError(error.response.data.data.error);
          setShowDeactivationModal(true);
          return;
        }

        // Add fallback logic if the API call fails
        if (!selectedSession && sessionDetails) {
          try {
            console.log("[Reading] Trying fallback session creation...");

            // Direct attempt without section lookup
            const sessionResponse = await axiosInstance.post(
              `${baseURL}/api/classroom/create-session`,
              {
                standard: sessionDetails.standard,
                section: sessionDetails.section || 204, // Use a default section ID (204 for standard 3)
                type: sessionDetails.type,
                // Use chapter id for textbook_chapter
                textbook_chapter:
                  sessionDetails.textbook_chapter ||
                  (sessionDetails.textbook?.chapters?.[0]?.id ?? null),
                textbook_id: sessionDetails.textbook_id, // Pass textbook_id if available
              },
            );

            const data = sessionResponse.data.data;
            const currentSessionId = data.session_id;
            const standardValue = data.standard;
            const textbookId = data.textbook_id;
            const textbookChapterId = data.textbook_chapter;

            console.log("[Reading] Fallback session created:", {
              sessionId: currentSessionId,
              standard: standardValue,
            });

            setSessionId(currentSessionId);
            setStandard(standardValue);

            // Fetch chapters using textbook_id and textbook_chapter from response
            if (textbookId && textbookChapterId) {
              try {
                const chaptersResponse = await axiosInstance.get(
                  `${baseURL}/api/classroom/get-classroom-chapters/?textbook_id=${textbookId}&chapter_id=${textbookChapterId}`,
                );
                setChapters(chaptersResponse.data.data);
                console.log(
                  "Chapters fetched successfully:",
                  chaptersResponse.data.data,
                );
              } catch (chapterErr) {
                console.error("Error fetching chapters:", chapterErr);
              }
            }
          } catch (secondError) {
            console.error("Fallback also failed:", secondError);
            // Check for deactivation error in fallback attempt
            if (
              secondError.response?.data?.data?.error?.includes(
                "has been deactivated",
              )
            ) {
              setDeactivationError(secondError.response.data.data.error);
              setShowDeactivationModal(true);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };

    if (access_token && (selectedSession || sessionDetails)) {
      console.log("[Reading] fetchData triggered with:", {
        selectedSession: selectedSession?.id,
        sessionDetails: !!sessionDetails,
        access_token: !!access_token,
      });
      fetchData();
    }
  }, [sessionDetails, selectedSession, access_token]);

  useEffect(() => {
    if (unifiedSession.standard && unifiedSession.section) {
      console.log("[Reading] sessionId state synchronized:", unifiedSession.id);
    }
  }, [unifiedSession.id]);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const active = selectedSession || sessionDetails;
        const normalizedType = (active?.type || "").toLowerCase();
        const isTextbook = normalizedType === "textbook";
        const isPassage = !isTextbook;

        // Early diagnostic log once per effect cycle
        console.log("[Reading] fetchStories trigger", {
          hasSelectedSession: !!selectedSession,
          hasSessionDetails: !!sessionDetails,
          selectedSessionType: selectedSession?.type,
          sessionDetailsType: sessionDetails?.type,
          activeType: active?.type,
          textbook_id: active?.textbook_id,
          textbook__id: active?.textbook__id,
          textbook_chapter: active?.textbook_chapter,
          textbook_chapter__id: active?.textbook_chapter__id,
          chaptersState: chapters,
          normalizedType,
          isPassage,
          isTextbook,
        });

        if (isPassage) {
          // Priority: 1. Locally updated standard (level switcher) 2. Session standard
          const standardName = standard || selectedSession?.standard__name;
          if (!standardName) return;

          const storyResponse = await axiosInstance.get(
            `${baseURL}/api/classroom/get-story?standard=${standardName}`,
          );

          // Randomize stories
          const storiesData = Array.isArray(storyResponse.data.data)
            ? [...storyResponse.data.data]
            : [];
          for (let i = storiesData.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [storiesData[i], storiesData[j]] = [storiesData[j], storiesData[i]];
          }
          setStories(storiesData);
        } else if (isTextbook) {
          // Check if we received a new data object directly in the request
          if (
            sessionDetails?.data?.reading_contents &&
            Array.isArray(sessionDetails.data.reading_contents)
          ) {
            // Use the reading_contents from the data passed directly from the request
            console.log(
              "Using reading_contents from data object:",
              sessionDetails.data.reading_contents,
            );

            // Randomize reading contents
            const textbookStories = [...sessionDetails.data.reading_contents];
            for (let i = textbookStories.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [textbookStories[i], textbookStories[j]] = [
                textbookStories[j],
                textbookStories[i],
              ];
            }

            setChapters(sessionDetails.data);
            setStories(textbookStories);
          }
          // For textbook type, check if chapters data exists
          else if (chapters?.reading_contents?.length > 0) {
            // Map chapter reading contents into the same structure as stories for consistency
            console.log(
              "Using reading_contents from chapters state:",
              chapters.reading_contents,
            );
            setStories(chapters.reading_contents);
          } else {
            // Try pulling ids from either sessionDetails or selectedSession
            const source = sessionDetails || selectedSession || {};
            const textbookIdParam = source.textbook_id ?? source.textbook__id;
            const chapterIdParam =
              source.textbook_chapter ?? source.textbook_chapter__id;
            console.log(
              "[Reading] Evaluating need for get-classroom-chapters",
              {
                textbookIdParam,
                chapterIdParam,
                haveChapters: !!chapters?.reading_contents?.length,
              },
            );
            if (
              textbookIdParam &&
              chapterIdParam &&
              !chapters?.reading_contents?.length
            ) {
              // If chapters aren't loaded yet but we have textbook and chapter IDs (any naming variant), fetch them
              try {
                console.log("Attempting get-classroom-chapters call with ids", {
                  textbookIdParam,
                  chapterIdParam,
                });
                const chaptersResponse = await axiosInstance.get(
                  `${baseURL}/api/classroom/get-classroom-chapters/?textbook_id=${textbookIdParam}&chapter_id=${chapterIdParam}`,
                );

                if (chaptersResponse.data.data?.reading_contents?.length > 0) {
                  console.log(
                    "Fetched reading_contents from API:",
                    chaptersResponse.data.data.reading_contents,
                  );
                  setChapters(chaptersResponse.data.data);
                  setStories(chaptersResponse.data.data.reading_contents);
                } else {
                  console.log(
                    "get-classroom-chapters succeeded but no reading_contents returned",
                    chaptersResponse.data.data,
                  );
                }
              } catch (chapterErr) {
                console.error(
                  "Error fetching textbook chapters (alt condition):",
                  chapterErr,
                );
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching reading content:", error);
      } finally {
        console.log(
          "[Reading] fetchStories finally block - setting loading to false",
        );
        setLoading(false);
      }
    };

    if (access_token && (selectedSession || sessionDetails)) {
      fetchStories();
    } else {
      console.log(
        "[Reading] fetchStories skipped (missing token/session) - clearing loading",
      );
      setLoading(false);
    }
  }, [
    access_token,
    selectedSession?.standard__name,
    standard,
    selectedSession?.type,
    sessionDetails?.type,
    selectedSession?.id,
    chapters,
    sessionDetails?.textbook_id,
    sessionDetails?.textbook_chapter,
    sessionDetails?.textbook__id,
    sessionDetails?.textbook_chapter__id,
    selectedSession?.textbook_id,
    selectedSession?.textbook__id,
    selectedSession?.textbook_chapter,
    selectedSession?.textbook_chapter__id,
  ]);

  // Updated Students fetching logic with correct field names
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        let currentSessionId = selectedSession?.id || sessionId;
        const sessionData = selectedSession || sessionDetails;

        if (!sessionData) {
          console.log("No session data available");
          return;
        }

        // Updated standard extraction to match backend field names
        const standardValue =
          sessionData?.standard__name || // From session object (backend field)
          sessionData?.standard || // Direct standard value
          standard || // From state
          null;

        // Don't proceed if we don't have a valid standard
        if (!standardValue) {
          console.error("No standard value found. Available data:", {
            sessionData,
            globalStandard: standard,
            selectedSession,
            sessionDetails,
          });
          return;
        }

        // Ensure standard is converted to string for API consistency
        const standardStr = String(standardValue);

        // Updated section extraction to match backend field names
        const sectionValue =
          sessionData?.section__name || // From session object (backend field)
          sessionData?.section_name || // Alternative field name
          sessionData?.section || // Direct section value
          "--"; // Default fallback

        const sectionStr = String(sectionValue);

        // Validate we have a session ID
        if (!currentSessionId) {
          console.error("No session ID available for fetching students");
          console.error("Available session IDs:", {
            selectedSessionId: selectedSession?.id,
            sessionIdState: sessionId,
            sessionData,
          });
          return;
        }

        console.log(
          "[Reading] Fetching students with session ID:",
          currentSessionId,
        );

        // Build URL with validated parameters
        const readingContentId = selectedTextbook?.id || storyId;
        let url = `${baseURL}/api/classroom/get-students?standard=${encodeURIComponent(standardStr)}&section=${encodeURIComponent(sectionStr)}&session_id=${currentSessionId}`;

        if (readingContentId) {
          url += `&reading_content_id=${readingContentId}`;
        }

        console.log("Making get-students API call with:", {
          standard: standardStr,
          section: sectionStr,
          sessionId: currentSessionId,
          readingContentId,
          url,
        });

        const studentsResponse = await axiosInstance.get(url);

        console.log("Students API response:", studentsResponse.data);
        setStudents(studentsResponse.data.data);
      } catch (error) {
        console.error("Error fetching students data:", error);
        console.error("Error details:", error.response?.data);

        // Additional error context
        if (error.response?.status === 400) {
          console.error(
            "Bad request - check if standard/section values are correct",
          );
        }
      }
    };

    if ((sessionDetails || selectedSession) && access_token) {
      fetchStudents();
    }
  }, [
    sessionDetails,
    selectedSession,
    access_token,
    apiFlag,
    sessionId,
    selectedTextbook,
    selectedStudent,
    storyId,
  ]);

  const activeSession = selectedSession || sessionDetails;

  // Map session type and ensure it is robust
  let sessionTypeLabel = "Reading Session";
  const normalizedType = (activeSession?.type || "").toLowerCase();
  const isTextbook = normalizedType === "textbook";
  // Default to passage if it's not explicitly textbook
  const isPassage = !isTextbook;

  if (isPassage) {
    sessionTypeLabel = "Story Session";
  } else if (isTextbook) {
    sessionTypeLabel = "Textbook Session";
  }
  if (selectedSession) {
    sessionTypeLabel = "Resumed " + sessionTypeLabel;
  }

  // Map chapter name
  // Step 3: Try to find chapter object from chapters using ID
  const chapterObj = Array.isArray(chapters)
    ? chapters.find(
        (ch) =>
          ch.id === activeSession?.textbook_chapter ||
          ch.chapter?.id === activeSession?.textbook_chapter,
      )?.chapter || chapters[0]?.chapter
    : chapters?.chapter;

  const chapterName =
    activeSession?.textbook_chapter__title ||
    activeSession?.textbook_chapter?.title ||
    activeSession?.chapter_title ||
    chapterObj?.title ||
    "--";

  // Add debug logging for props being passed to StoryReading
  useEffect(() => {
    console.log("[Reading] Passing props to StoryReading:", {
      sessionId,
      selectedSessionId: selectedSession?.id,
      studentsCount: students.length,
      storiesCount: stories.length,
      chapterName,
      normalizedType,
      isPassage,
      isTextbook,
    });
  }, [sessionId, selectedSession, students, stories]);

  return (
    <div className="h-full overflow-y-auto min-h-0 w-full p-1 flex flex-col">
      {/* Main Content */}
      {isTextbook && (
        <div className="flex h-auto gap-1 items-stretch">
          {Loading && <LoadingScreen />}

          {/* Reading Content */}
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <StoryReading
              textbooks={textbooks}
              selectedTextbook={selectedTextbook}
              wsRef={wsRef}
              unifiedSession={unifiedSession}
              selectedStudent={selectedStudent}
              socketres={socketres}
              setSocketres={setSocketres}
              setLoading={setLoading}
              apiResponseMiddle={apiResponseMiddle}
              setapiResponseMiddle={setapiResponseMiddle}
              apiFlag={apiFlag}
              setApiFlag={setApiFlag}
              stories={stories}
              setStoryId={setStoryId}
              storyId={storyId}
              studentflag={studentflag}
              students={students}
              speecherror={speecherror}
              readingContentError={readingContentError}
              setReadingContentError={setReadingContentError}
              loading={Loading}
              isTextbookMode={true}
              creditsRemaining={creditsRemaining}
              refreshCredits={refreshCredits}
              showRecordingCountdown={showRecordingCountdown}
            />
          </div>

          {/* Levels Pagination */}
          <div className="w-fit flex justify-center">
            <LevelsPagination
              activeGrade={unifiedSession.standard}
              onLevelChange={handleGradeSwitch}
            />
          </div>

          {/* Student List */}
          <div className="w-full lg:w-64 bg-white/80 backdrop-blur-md rounded-lg shadow-sm flex flex-col overflow-hidden">
            <div className="bg-blue-600 text-white p-3 font-medium flex justify-between items-center flex-shrink-0">
              <span>Students</span>
              <span className="text-sm">({students.length})</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <Student_List
                students={students}
                setSelectedStudent={setSelectedStudent}
                selectedStudent={selectedStudent}
                setStudentFlag={setStudentFlag}
              />
            </div>
          </div>
        </div>
      )}

      {isPassage && (
        <div className="flex flex-col md:flex-row gap-2 items-stretch">
          {Loading && <LoadingScreen />}

          {/* Reading Content */}
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <StoryReading
              textbooks={textbooks}
              selectedTextbook={selectedTextbook}
              wsRef={wsRef}
              unifiedSession={unifiedSession}
              selectedStudent={selectedStudent}
              socketres={socketres}
              setSocketres={setSocketres}
              setLoading={setLoading}
              apiResponseMiddle={apiResponseMiddle}
              setapiResponseMiddle={setapiResponseMiddle}
              apiFlag={apiFlag}
              setApiFlag={setApiFlag}
              stories={stories}
              setStoryId={setStoryId}
              storyId={storyId}
              studentflag={studentflag}
              students={students}
              speecherror={speecherror}
              readingContentError={readingContentError}
              setReadingContentError={setReadingContentError}
              loading={Loading}
              isTextbookMode={false}
              creditsRemaining={creditsRemaining}
              refreshCredits={refreshCredits}
              showRecordingCountdown={showRecordingCountdown}
            />
          </div>

          {/* Levels Pagination */}
          <div className="w-fit flex justify-center">
            <LevelsPagination
              activeGrade={unifiedSession.standard}
              onLevelChange={handleGradeSwitch}
            />
          </div>

          {/* Student List */}
          <div className="w-48 xl:w-64 bg-white/80 backdrop-blur-md rounded-lg shadow-sm flex flex-col h-[80vh] overflow-hidden">
            <div className="bg-blue-600 text-white p-3 font-medium flex justify-between items-center flex-shrink-0">
              <span>Students</span>
              <span className="text-sm">({students.length})</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <Student_List
                students={students}
                setSelectedStudent={setSelectedStudent}
                selectedStudent={selectedStudent}
                setStudentFlag={setStudentFlag}
              />
            </div>
          </div>
        </div>
      )}

      {/* Deactivation Error Modal */}
      <Dialog
        open={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <div style={{ position: "relative" }}>
          <div
            style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ color: "#1f2937" }}>
                Account Deactivation Notice
              </Typography>
              <IconButton
                onClick={() => setShowDeactivationModal(false)}
                sx={{ color: "#9ca3af", "&:hover": { color: "#4b5563" } }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent sx={{ p: 3 }}>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 bg-red-50 text-red-700 p-4 rounded-lg">
                <ErrorIcon className="h-5 w-5 text-red-500" />
                <p className="font-medium">{deactivationError}</p>
              </div>
              <Typography variant="body1" sx={{ mt: 2 }}>
                Please contact your administrator to renew your account access.
              </Typography>
            </div>
          </DialogContent>

          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              padding: "16px 24px",
              backgroundColor: "#f9fafb",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setShowDeactivationModal(false)}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {showScoreAnimation !== null && (
        <ScoreAnimation
          score={showScoreAnimation}
          onClose={() => setShowScoreAnimation(null)}
        />
      )}
    </div>
  );
};

export default Reading;
